import React from "react";
import { Link } from "react-router-dom";
import { PlusIcon, CheckIcon, ArrowRightIcon } from "lucide-react";
import { ingredients } from "../../data/ingredients";
import { categories } from "../../data/categories";
import { useQuote } from "../../contexts/QuoteContext";
import { Availability, CategoryId, Ingredient } from "../../types/ingredient";
interface IngredientCatalogueProps {
  activeCategory: CategoryId | "all";
  onCategoryChange: (id: CategoryId | "all") => void;
}
const availabilityStyle: Record<Availability, string> = {
  stock: "bg-moss",
  limited: "bg-terracotta",
  preorder: "bg-lavender"
};
export function IngredientCatalogue({
  activeCategory,
  onCategoryChange
}: IngredientCatalogueProps) {
  const visible = activeCategory === "all" ? ingredients : ingredients.filter(i => i.category === activeCategory);
  const tabs: {
    id: CategoryId | "all";
    label: string;
  }[] = [{
    id: "all",
    label: "Tout"
  }, ...categories.map(c => ({
    id: c.id,
    label: c.name
  }))];
  return <section id="catalogue" className="scroll-mt-20 border-t border-line bg-paper" data-id="element-118" data-mp="rutjf433vgbq">
      <div className="mx-auto max-w-[1400px] px-5 pb-24 pt-20 md:px-10 md:pb-32" data-id="element-119" data-mp="vxq91er2ccp6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-120" data-mp="hxyp6xdv8239">
          <div data-id="element-121" data-mp="mws50wgmk56l">
            <h2 className="font-display text-4xl tracking-tight text-ink md:text-5xl" data-id="element-122" data-mp="mw9z1845uclh">Disponible cette saison</h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-stone" data-id="element-123" data-mp="1am4bvpohc15q">
              Ajoutez les références qui vous intéressent à votre sélection : nous chiffrons l'ensemble en une seule
              réponse, échantillons compris.
            </p>
          </div>
          <Link to="/ingredients" className="flex items-center gap-2 whitespace-nowrap text-sm font-medium text-terracotta hover:text-terracotta-dark" data-id="element-124" data-mp="15ve4k6whto8e">
            Catalogue complet <ArrowRightIcon className="h-4 w-4" data-id="element-125" data-mp="1tj5fb62dxt7k" />
          </Link>
        </div>

        <div className="-mx-5 mt-10 overflow-x-auto px-5 md:mx-0 md:px-0" data-id="element-126" data-mp="1ydjg5nztgsjm">
          <div role="tablist" aria-label="Filtrer par famille" className="flex w-max gap-2" data-id="element-127" data-mp="286w1yaqxgjc1">
            {tabs.map(tab => {
            const selected = tab.id === activeCategory;
            return <button key={tab.id} role="tab" aria-selected={selected} onClick={() => onCategoryChange(tab.id)} className={`h-10 whitespace-nowrap rounded-full px-4 text-sm transition-colors duration-150 ease-out ${selected ? "bg-ink text-paper" : "border border-line text-olive hover:bg-cream"}`} data-id="element-128" data-mp="1y8g9a3c9520f">
                  {tab.label}
                </button>;
          })}
          </div>
        </div>

        <div className="mt-8" data-id="element-129" data-mp="16ibs0djsgv3f">
          <div className="hidden grid-cols-[2.2fr_1.1fr_1fr_1.4fr_1fr_auto] gap-6 border-b border-ink pb-3 text-xs text-stone lg:grid" data-id="element-130" data-mp="16hjzjblhndhd">
            <span data-id="element-131" data-mp="11llbxq7zhzi0">Ingrédient · INCI</span>
            <span data-id="element-132" data-mp="3d7dkkkgeqqa9">Famille</span>
            <span data-id="element-133" data-mp="3d6c4g1kjbdlo">Certification</span>
            <span data-id="element-134" data-mp="3i5x46e7j3pzg">Conditionnement</span>
            <span data-id="element-135" data-mp="3i4vtiaufd5i9">Disponibilité</span>
            <span className="w-[132px]" data-id="element-136" data-mp="3i81i7oz565dg" />
          </div>
          {visible.length === 0 ? <p className="py-12 text-sm text-stone" data-id="element-137" data-mp="3m8bkornq6dnk">Aucune référence en vedette dans cette famille pour le moment.</p> : <ul data-id="element-138" data-mp="2ugmb7iwf6zvv">
              {visible.map(ing => <IngredientRow key={ing.id} ingredient={ing} data-id="element-139" data-mp="2ui76jaqk6mus" />)}
            </ul>}
        </div>
      </div>
    </section>;
}
function IngredientRow({
  ingredient: ing
}: {
  ingredient: Ingredient;
}) {
  const {
    has,
    toggle
  } = useQuote();
  const added = has(ing.id);
  const family = categories.find(c => c.id === ing.category)?.name;
  return <li className="grid grid-cols-[1fr_auto] items-center gap-x-6 gap-y-2 border-b border-line py-5 lg:grid-cols-[2.2fr_1.1fr_1fr_1.4fr_1fr_auto]" data-id="element-140" data-mp="2arbz0evvux2i">
      <div className="min-w-0" data-id="element-141" data-mp="2fp3o71558530">
        <p className="font-display text-2xl leading-tight text-ink" data-id="element-142" data-mp="m7xytwglt4y0">{ing.name}</p>
        <p className="mt-1 truncate text-xs italic text-stone" data-id="element-143" data-mp="h9wfdaf9n3m0">{ing.inci}</p>
      </div>
      <span className="hidden text-sm text-olive lg:block" data-id="element-144" data-mp="v117lrgb0tmb">{family}</span>
      <span className="hidden text-sm text-olive lg:block" data-id="element-145" data-mp="v2ccxvpee9fj">{ing.certification}</span>
      <span className="hidden text-sm text-olive lg:block" data-id="element-146" data-mp="q8rnwt5s8psh">
        {ing.packaging}
        <span className="block text-xs text-stone" data-id="element-147" data-mp="14y5trra5gp34">MOQ {ing.moq}</span>
      </span>
      <span className="hidden items-center gap-2 text-sm text-olive lg:flex" data-id="element-148" data-mp="100xdjw8kav4i">
        <span className={`h-2 w-2 shrink-0 rounded-full ${availabilityStyle[ing.availability]}`} aria-hidden data-id="element-149" data-mp="1dznbfe37gytx" />
        {ing.availabilityLabel}
      </span>
      <button type="button" onClick={() => toggle({
      id: ing.id,
      name: ing.name,
      inci: ing.inci
    })} aria-pressed={added} className={`row-span-2 flex h-10 w-[132px] items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm transition-colors duration-150 ease-out active:scale-[0.97] lg:row-span-1 ${added ? "bg-olive text-paper hover:bg-ink" : "border border-ink text-ink hover:bg-ink hover:text-paper"}`} data-id="element-150" data-mp="19ytpurd8jw47">
        {added ? <CheckIcon className="h-4 w-4" data-id="element-151" data-mp="2rv5c0nf0ow7q" /> : <PlusIcon className="h-4 w-4" data-id="element-152" data-mp="2ru41ql5tsdr2" />}
        {added ? "Ajouté" : "Au devis"}
      </button>
      <p className="flex items-center gap-2 text-xs text-stone lg:hidden" data-id="element-153" data-mp="2wsoszv33nn3d">
        <span className={`h-2 w-2 rounded-full ${availabilityStyle[ing.availability]}`} aria-hidden data-id="element-154" data-mp="2hw7l5du4cqlk" />
        {ing.availabilityLabel} · {ing.certification} · MOQ {ing.moq}
      </p>
    </li>;
}